import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Video, Download, ExternalLink, AlertTriangle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ExportVideo() {
  const [renderKey, setRenderKey] = useState(localStorage.getItem("render_key") || "");
  const [estado, setEstado] = useState("idle"); // idle | processing | done | error
  const [jobId, setJobId] = useState(null);
  const [descarga, setDescarga] = useState(null);
  const [error, setError] = useState(null);
  const [tam, setTam] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${BACKEND}/api/render-showcase/${id}`);
        const d = await r.json();
        if (d.status === "done") {
          clearInterval(pollRef.current);
          setEstado("done");
          setDescarga(`${BACKEND}${d.download_url}`);
          setTam(d.size_mb || null);
        } else if (d.status === "error") {
          clearInterval(pollRef.current);
          setEstado("error");
          setError(d.error || "Error desconocido al renderizar.");
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setEstado("error");
        setError("No se pudo consultar el estado del render.");
      }
    }, 3000);
  };

  const generar = async () => {
    setEstado("processing");
    setError(null);
    setDescarga(null);
    setTam(null);
    localStorage.setItem("render_key", renderKey);
    try {
      const r = await fetch(`${BACKEND}/api/render-showcase`, {
        method: "POST",
        headers: { "X-Render-Key": renderKey },
      });
      const d = await r.json();
      if (!r.ok) {
        setEstado("error");
        setError(d.detail || "No se pudo iniciar el render.");
        return;
      }
      setJobId(d.job_id);
      poll(d.job_id);
    } catch (e) {
      setEstado("error");
      setError("No se pudo contactar con el backend.");
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 space-y-6" data-testid="export-video-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight text-[#211c18]">Exportar vídeo de la demo</h1>
        <p className="mt-2 text-muted-foreground">
          Genera un MP4 1920×1080 de <code>/showcase</code> en el servidor (sin grabar pantalla).
          El renderizado tarda alrededor de un minuto.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Video className="w-5 h-5 text-[#FF5625]" /> Generar vídeo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#211c18]">Clave de render (X-Render-Key)</label>
            <Input
              type="password"
              value={renderKey}
              onChange={(e) => setRenderKey(e.target.value)}
              placeholder="Pega aquí la clave RENDER_KEY"
              className="mt-1"
              data-testid="render-key-input"
            />
          </div>

          <Button
            onClick={generar}
            disabled={!renderKey || estado === "processing"}
            className="w-full bg-[#FF5625] hover:bg-[#e04a1e] text-white font-bold"
            data-testid="generar-video-btn"
          >
            {estado === "processing" ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Renderizando… (~1 min)</>
            ) : (
              <><Video className="w-4 h-4 mr-2" /> Generar vídeo</>
            )}
          </Button>

          {estado === "processing" && (
            <p className="text-sm text-muted-foreground" data-testid="export-status">
              Grabando las 8 escenas y convirtiendo a MP4… No cierres esta pestaña.
            </p>
          )}

          {estado === "done" && descarga && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3" data-testid="export-done">
              <p className="text-sm font-semibold text-green-800">
                ✅ Vídeo listo{tam ? ` · ${tam} MB` : ""}.
              </p>
              <div className="flex gap-3">
                <a href={descarga} download="auditready-showcase.mp4" data-testid="download-link">
                  <Button className="bg-[#211c18] hover:bg-black text-white font-bold">
                    <Download className="w-4 h-4 mr-2" /> Descargar MP4
                  </Button>
                </a>
                <a href={descarga} target="_blank" rel="noreferrer">
                  <Button variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" /> Abrir en pestaña
                  </Button>
                </a>
              </div>
            </div>
          )}

          {estado === "error" && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="export-error">
              <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Error al generar el vídeo
              </p>
              <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap break-words">{error}</pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
